import { assertContentWriteReadback } from '@modules/content-plan/services/contentConsumerTransport';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
/**
 * TopicBacklogFirestoreService — CRUD for the project-level topic backlog at:
 *   projects/{pid}/topic_backlog/{topicId}
 *
 * Mirrors `AuthorContentCorpusService.deleteAuthorPosts` for parity:
 * chunked bulk-delete at 400 docs/batch (Firestore limit 500, headroom for
 * future serverTimestamp ops on a sibling write).
 *
 * Real-time read still goes through `useTopicBacklog`; this service is the
 * write side (delete + status update from BacklogTab UI actions).
 */

import {
  collection,
  doc,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { systemPlatformService } from '@modules/content-plan/services/SystemPlatformService';

const BATCH_CHUNK_SIZE = 400;

export class TopicBacklogFirestoreService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private topicsCollection(projectId: string) {
    return collection(this.db, 'projects', projectId, 'topic_backlog');
  }

  /**
   * Bulk-delete topics by id. Chunked at 400 ops per batch.
   * Idempotent — deleting a non-existent doc is a no-op in Firestore batch.
   */
  async deleteTopics(projectId: string, topicIds: string[]): Promise<void> {
    if (topicIds.length === 0) return;
    const col = this.topicsCollection(projectId);
    for (let i = 0; i < topicIds.length; i += BATCH_CHUNK_SIZE) {
      const chunk = topicIds.slice(i, i + BATCH_CHUNK_SIZE);
      const batch = writeBatch(this.db);
      for (const id of chunk) {
        batch.delete(doc(col, id));
      }
      await batch.commit();
    }
  }

  /** Promote a single draft topic to ready (used by quick action). */
  async setStatus(
    projectId: string,
    topicId: string,
    status: 'draft' | 'ready' | 'evergreen' | 'assigned' | 'archived' | 'banned',
  ): Promise<void> {
    await this.update(projectId, topicId, { status });
  }

  /**
   * Patch arbitrary fields on a topic. Used by Topic Detail editors
   * (postBreakdown, plannedPostCount, defaultTemplateIds, etc).
   */
  async update(
    projectId: string,
    topicId: string,
    patch: Record<string, unknown>,
  ): Promise<void> {
    if (Array.isArray(patch.targetPlatforms)) {
      const catalog = await systemPlatformService.listPlatforms({ activeOnly: true });
      const known = new Set(catalog.map((platform) => platform.platformId));
      const unknown = patch.targetPlatforms
        .map((platform) => String(platform))
        .filter((platform) => !known.has(platform));
      if (unknown.length > 0) {
        throw new Error(`Unknown system platform(s): ${unknown.join(', ')}. Add them in Admin → System Platforms first.`);
      }
    }
    const ref = doc(this.topicsCollection(projectId), topicId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Topic no longer exists.');
    const authorId = current.data().authorId;
    if (typeof authorId !== 'string' || !authorId.trim()) throw new Error('Topic author is required before saving.');
    await new ContentPlanApiClient().patchTopicRecord(projectId, topicId, authorId, patch);
    assertContentWriteReadback((await getDoc(ref)).data(), patch);
  }
}

export const topicBacklogFirestoreService = new TopicBacklogFirestoreService();
